/**
 * projectMetrics.js — shared canonical helpers for job-level metrics.
 *
 * Phase 5.5 R3 audit fix (M3): Unify the `batchDone > 0 ? batchDone :
 * assignDone` pattern that had independently reappeared in 3+ files with
 * subtly different rules. All consumers now route through
 * `resolveAnnotationsDone` so the semantics are maintained in exactly
 * one place — if the batch-system's reliability characteristics change,
 * there's a single function to update.
 *
 * Pure helpers only. No DB, no React, no Supabase. Safe to import from
 * any layer.
 */

/**
 * Canonical resolver for "how many annotations are done on this job."
 *
 * Prefers the batch-system count (live, per-item-tracked, reliable) over
 * the legacy `assignment.annotations_completed` aggregate (can be stale
 * from seed data or from the pre-batch migration window). Falls back to
 * the assignment aggregate only when no batch-system data exists yet —
 * typically a brand-new job where batches haven't been created.
 *
 * Why this matters: `assignment.annotations_completed` is a denormalized
 * rolling counter updated by older code paths. Seed data commonly
 * populates it with lifetime values (e.g., 1170 items from migration
 * 041), which if trusted blindly would make a brand-new job look already
 * done. Batch-system counts are the source of truth for live jobs.
 *
 * @param {string} jobId
 * @param {Record<string, number>|null|undefined} batchSummary
 *        `{ [jobId]: itemsCompleted }` lookup — output of
 *        batchService.getBatchSummary or the equivalent. Null-safe.
 * @param {Array<{job_id: string, annotations_completed: number}>} assignments
 *        Full assignment list; filtered internally to jobId. Null-safe.
 * @returns {number} non-negative integer count
 */
export function resolveAnnotationsDone(jobId, batchSummary, assignments) {
  const batchDone = Number(batchSummary?.[jobId]) || 0;
  if (batchDone > 0) return batchDone;
  if (!Array.isArray(assignments)) return 0;
  const assignDone = assignments
    .filter((a) => a?.job_id === jobId)
    .reduce((sum, a) => sum + (Number(a?.annotations_completed) || 0), 0);
  return Math.max(0, assignDone);
}

/**
 * Canonical resolver for "how many items are still remaining" on a job.
 *
 * Semantics: items_remaining = max(0, annotations_target - annotations_done).
 *
 * NOTE on in-progress + in-review items (R3 audit H3, follow-up):
 *   Currently we treat (target - done) as remaining. In-progress items
 *   aren't double-counted because they become "done" at throughput rate
 *   — the projection naturally consumes them via team weekly output.
 *   In-review items waiting on reviewer capacity are a separate concern
 *   (review-queue throughput ≠ annotation throughput); Phase 6 will
 *   model this explicitly once multi-stage pipelines are designed.
 *
 * @param {number} annotationsTarget
 * @param {number} annotationsDone
 * @returns {number} non-negative integer remaining
 */
export function resolveItemsRemaining(annotationsTarget, annotationsDone) {
  const target = Number(annotationsTarget) || 0;
  const done = Number(annotationsDone) || 0;
  return Math.max(0, target - done);
}

/**
 * Canonical resolver for "what rate do we bill an assignment at."
 *
 * R6 MED-1 audit fix: previously 5 call sites used 5 different formulas:
 *   - projectJob.js:                `a.rate ?? a.confirmed_rate || 0`    (R4 LOW-5)
 *   - computeFinancials.js:         `a.confirmed_rate || a.rate || 0`    (legacy `||`)
 *   - AdminProjects.jsx:            `a.rate || 0`                         (no fallback)
 *   - SPLDashboard.jsx:             `a.rate || 0`                         (no fallback)
 *   - ProjectDashboard legacy:      `a.confirmed_rate || a.rate || base_rate`
 *
 * A single assignment with rate=0 + confirmed_rate=25 showed up as $0 on
 * some views and $25 on others — same job, different "spent" across
 * dashboards. This canonical resolver is the single source of truth.
 *
 * Semantics (R4 LOW-5 already-correct choice):
 *   - `??` not `||` — a legitimate rate=0 (unpaid intern, pro bono
 *     contributor, zero-rate job) is NOT replaced by confirmed_rate.
 *     Only nullish (null/undefined) falls through.
 *   - `confirmed_rate` is the assignment-level attested rate from an
 *     SPL; `rate` is the raw assignment rate (may differ if the SPL
 *     confirmed a rate different from the posted job rate).
 *   - Final fallback 0 (not job.base_rate) — if neither is set, the
 *     assignment contributes $0 to cost, which is correct semantic
 *     ("rate unknown → don't guess").
 *
 * @param {object} assignment — may be null/undefined
 * @returns {number} non-negative rate (0 if both nullish)
 */
export function resolveAssignmentRate(assignment) {
  if (!assignment) return 0;
  // R8 BLOCKER-1 audit fix: the R7 two-stage resolver had a silent
  // regression — `Number(null) === 0` (not NaN), so `null` rates
  // passed `isFinite(0) && 0 >= 0` and returned 0 directly,
  // NEVER falling through to confirmed_rate. This un-fixed R4 LOW-5
  // for any `rate=null, confirmed_rate=N` assignment — the exact
  // "nullish falls through" semantic the `??` operator was chosen
  // for. Fix: gate the first-stage numeric coercion behind an
  // explicit `!= null` check (loose equality, catches both null and
  // undefined). Empty-string and non-numeric-string cases still
  // fall through via the `isFinite` check. Legitimate rate=0
  // (unpaid / pro bono) is preserved because `0 != null` is true
  // and `Number(0) = 0` passes isFinite.
  // R9 LOW-2 audit fix: treat empty-string as equivalent to null.
  // Some form-clear paths serialize an empty input as "" instead of null;
  // the R4 LOW-5 intent ("nullish falls through") should cover this case
  // even though `"" != null` is technically true.
  if (assignment.rate != null && assignment.rate !== '') {
    const rRate = Number(assignment.rate);
    if (Number.isFinite(rRate) && rRate >= 0) return rRate;
  }
  if (assignment.confirmed_rate == null || assignment.confirmed_rate === '') return 0;
  const rConf = Number(assignment.confirmed_rate);
  return Number.isFinite(rConf) && rConf >= 0 ? rConf : 0;
}

/**
 * Canonical resolver for "how many weeks of observational history do we
 * have on this project." Used as confidence-tier input to projections.
 *
 * R3 audit fix (M1): The prior logic took median(annotator tenures),
 * which mis-classified long-running projects with recent team rotation
 * (a 12-week project with 2 new hires would have median tenure 0.03
 * weeks — "insufficient data" tier, despite 12 weeks of batch history).
 * We now return `max(medianTenure, projectAgeWeeks)` so staffed-rotation
 * doesn't discard actual project history.
 *
 * Null-safe: returns 0 if no data.
 *
 * @param {Array<{weeksActive: number}>} velocities — output of computeAnnotatorVelocities
 * @param {Date|string|number|null} projectStartDate
 * @param {Date} [now] — injectable for test stability
 * @returns {number} weeks of history (fractional OK)
 */
export function resolveWeeksOfHistory(velocities, projectStartDate, now = new Date()) {
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  const tenures = Array.isArray(velocities)
    ? velocities.map((v) => Number(v?.weeksActive) || 0).filter((n) => n > 0).sort((a, b) => a - b)
    : [];
  let medianTenure = 0;
  if (tenures.length > 0) {
    const mid = (tenures.length - 1) / 2;
    if (Number.isInteger(mid)) {
      medianTenure = tenures[mid];
    } else {
      medianTenure = (tenures[Math.floor(mid)] + tenures[Math.ceil(mid)]) / 2;
    }
  }
  let projectAgeWeeks = 0;
  if (projectStartDate != null) {
    const startMs = projectStartDate instanceof Date
      ? projectStartDate.getTime()
      : new Date(projectStartDate).getTime();
    const nowMs = now instanceof Date ? now.getTime() : Number(now);
    // R3 L3 audit fix: sanity-cap at 520 weeks (~10 years). A seeded-as-
    // epoch start date would otherwise make projectAgeWeeks ≈ 3100 weeks
    // and trivially pass every confidence threshold (misrepresenting a
    // brand-new project with corrupted metadata as having years of
    // observational history).
    const MAX_PROJECT_AGE_WEEKS = 520;
    if (Number.isFinite(startMs) && Number.isFinite(nowMs) && nowMs > startMs) {
      projectAgeWeeks = Math.min(MAX_PROJECT_AGE_WEEKS, (nowMs - startMs) / MS_PER_WEEK);
    }
  }
  return Math.max(0, medianTenure, projectAgeWeeks);
}
